import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { toDatabaseValue } from "@/utils/supabase/type-guards";
import { useCreateNotification } from "@/hooks/use-create-notification";

export const usePromptSave = (promptId: string, user: any, initialSaved = false, onUnsave?: () => void) => {
  const [isSaved, setIsSaved] = useState(initialSaved);
  const { notifyOnSave } = useCreateNotification();

  useEffect(() => {
    if (!user) return;
    
    const checkSaveStatus = async () => {
      try {
        const { data: saveData, error: saveError } = await supabase
          .from('saved_prompts')
          .select('id')
          .eq('prompt_id', toDatabaseValue(promptId))
          .eq('user_id', toDatabaseValue(user.id))
          .maybeSingle();
          
        if (!saveError) {
          setIsSaved(!!saveData);
        }
      } catch (error) {
        console.error('Error checking save status:', error);
      }
    };
    
    checkSaveStatus();
  }, [promptId, user]);

  const handleSave = async (newSavedState: boolean) => {
    if (!user) {
      toast.error("Please sign in to save prompts");
      return;
    }
    
    try {
      if (newSavedState) {
        const { error } = await supabase
          .from('saved_prompts')
          .upsert(
            toDatabaseValue({ user_id: user.id, prompt_id: promptId }),
            { onConflict: 'user_id,prompt_id', ignoreDuplicates: true }
          );
          
        if (error) throw error;
        
        setIsSaved(true);
        toast.success("Prompt saved");

        // Get username using public_profiles view for security
        const { data: profile } = await supabase
          .from('public_profiles')
          .select('username')
          .eq('id', user.id)
          .single();
        
        if (profile?.username) {
          notifyOnSave(promptId, user.id, profile.username);
        }
      } else {
        const { error } = await supabase
          .from('saved_prompts')
          .delete()
          .eq('user_id', toDatabaseValue(user.id))
          .eq('prompt_id', toDatabaseValue(promptId));
          
        if (error) throw error;
        
        setIsSaved(false);
        toast.success("Prompt removed from saved");
        
        if (onUnsave) {
          onUnsave();
        }
      }
    } catch (error: any) {
      console.error('Error toggling save:', error);
      toast.error(error.message || "Failed to update save status");
    }
  };

  return {
    isSaved,
    handleSave,
    setIsSaved
  };
};
